import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const BTN = 'border px-[0.85rem] py-[0.45rem] rounded-sm cursor-pointer font-[inherit] text-[0.80rem] transition-colors';
const BTN_CANCEL = `${BTN} bg-transparent border-border-strong text-text-muted hover:text-text hover:border-accent-gold`;

const TONES = {
  default: {
    panel: 'border-border-strong',
    eyebrow: 'text-accent-gold',
    confirm: `${BTN} bg-[rgba(240,180,41,0.12)] border-accent-gold text-accent-gold hover:bg-[rgba(240,180,41,0.20)]`,
  },
  danger: {
    panel: 'border-[rgba(248,113,113,0.45)]',
    eyebrow: 'text-accent-red',
    confirm: `${BTN} bg-[rgba(248,113,113,0.12)] border-accent-red text-accent-red hover:bg-[rgba(248,113,113,0.20)]`,
  },
  info: {
    panel: 'border-[rgba(96,165,250,0.45)]',
    eyebrow: 'text-accent-blue',
    confirm: `${BTN} bg-[rgba(96,165,250,0.12)] border-accent-blue text-accent-blue hover:bg-[rgba(96,165,250,0.20)]`,
  },
};

export default function AppDialog({
  open,
  title,
  message,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  onConfirm,
  onCancel,
}) {
  const titleId = useId();
  const bodyId = useId();
  const cancelRef = useRef(null);
  const body = description ?? message;
  const styles = TONES[tone] ?? TONES.default;

  useEffect(() => {
    if (!open || typeof window === 'undefined') return undefined;

    const previousFocus = document.activeElement;
    const focusTimer = window.setTimeout(() => cancelRef.current?.focus(), 0);
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onCancel?.();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', onKeyDown);
      previousFocus?.focus?.();
    };
  }, [open, onCancel]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[220] flex items-start justify-center overflow-y-auto overscroll-contain bg-black/60 px-4 py-4 backdrop-blur-[4px] sm:py-8"
      onClick={onCancel}
    >
      <div
        className={`flex w-full min-w-0 max-w-[440px] max-h-[calc(100vh-2rem)] flex-col overflow-hidden rounded-lg border ${styles.panel} bg-surface-el shadow-lg`}
        role={tone === 'danger' ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={body ? bodyId : undefined}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="shrink-0 border-b border-border px-4 py-3">
          <span className={`mb-1 block text-[0.66rem] font-semibold uppercase tracking-[0.10em] ${styles.eyebrow}`}>
            Confirm action
          </span>
          <h3 id={titleId} className="m-0 text-base font-semibold text-text">
            {title}
          </h3>
        </div>
        {body && (
          <div id={bodyId} className="min-h-0 overflow-y-auto overscroll-contain px-4 py-3 text-[0.84rem] leading-[1.55] text-text-muted">
            {body}
          </div>
        )}
        <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-border bg-surface/70 px-4 py-3">
          <button type="button" ref={cancelRef} className={BTN_CANCEL} onClick={onCancel}>
            {cancelLabel}
          </button>
          <button type="button" className={styles.confirm} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function useAppConfirm() {
  const [options, setOptions] = useState(null);
  const resolverRef = useRef(null);

  const close = useCallback((result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setOptions(null);
    resolve?.(result);
  }, []);

  const requestConfirmation = useCallback((nextOptions) => new Promise((resolve) => {
    resolverRef.current?.(false);
    resolverRef.current = resolve;
    setOptions(nextOptions ?? {});
  }), []);

  useEffect(() => () => {
    resolverRef.current?.(false);
    resolverRef.current = null;
  }, []);

  return {
    requestConfirmation,
    dialog: options ? (
      <AppDialog
        open
        {...options}
        onConfirm={() => close(true)}
        onCancel={() => close(false)}
      />
    ) : null,
  };
}
